import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  Megaphone,
  Heart,
  MessageCircle,
  Eye,
  Pin,
  Send,
  Plus,
  Trash2,
  Edit,
  Clock,
  ChevronRight,
  Loader2,
  Filter
} from 'lucide-react';

export default function BroadcastsPage() {
  const { token, user } = useAuth();
  const [broadcasts, setBroadcasts] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedBroadcast, setSelectedBroadcast] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  
  // Admin check
  const isAdmin = user?.role === 'admin' || user?.role === 'owner' || user?.email === 'admin@bodiesbyakeem.com';
  
  // Create form state
  const [createForm, setCreateForm] = useState({
    title: '',
    content: '',
    category: 'general',
    image_url: '',
    is_pinned: false,
    target_audience: 'all'
  });
  const [creating, setCreating] = useState(false);

  const fetchBroadcasts = useCallback(async () => {
    try {
      const params = filterCategory !== 'all' ? `?category=${filterCategory}` : '';
      const response = await axios.get(`${API}/broadcasts${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBroadcasts(response.data.broadcasts || []);
    } catch (error) {
      console.error('Error fetching broadcasts:', error);
    } finally {
      setLoading(false);
    }
  }, [token, filterCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/broadcasts/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchBroadcastDetail = async (id) => {
    try {
      const response = await axios.get(`${API}/broadcasts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedBroadcast(response.data);
    } catch (error) {
      toast.error('Failed to load broadcast');
    }
  };

  useEffect(() => {
    fetchBroadcasts();
    fetchCategories();
  }, [fetchBroadcasts]);

  const handleLike = async (broadcastId, e) => {
    e?.stopPropagation();
    try {
      const response = await axios.post(
        `${API}/broadcasts/${broadcastId}/like`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setBroadcasts(prev => prev.map(b => 
        b.id === broadcastId 
          ? { ...b, is_liked: response.data.liked, likes_count: b.likes_count + (response.data.liked ? 1 : -1) }
          : b
      ));
      
      if (selectedBroadcast?.id === broadcastId) {
        setSelectedBroadcast(prev => ({
          ...prev,
          is_liked: response.data.liked,
          likes_count: prev.likes_count + (response.data.liked ? 1 : -1)
        }));
      }
    } catch (error) {
      toast.error('Failed to like');
    }
  };

  const handleComment = async () => {
    if (!newComment.trim() || !selectedBroadcast) return;
    
    setSubmittingComment(true);
    try {
      await axios.post(
        `${API}/broadcasts/${selectedBroadcast.id}/comment?content=${encodeURIComponent(newComment)}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Comment added');
      setNewComment('');
      fetchBroadcastDetail(selectedBroadcast.id);
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(
        `${API}/broadcasts/${selectedBroadcast.id}/comment/${commentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Comment deleted');
      fetchBroadcastDetail(selectedBroadcast.id);
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const handleLikeComment = async (commentId) => {
    try {
      await axios.post(
        `${API}/broadcasts/${selectedBroadcast.id}/comment/${commentId}/like`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchBroadcastDetail(selectedBroadcast.id);
    } catch (error) {
      toast.error('Failed to like comment');
    }
  };

  const handleCreateBroadcast = async () => {
    if (!createForm.title.trim() || !createForm.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    
    setCreating(true);
    try {
      const params = new URLSearchParams({
        title: createForm.title,
        content: createForm.content,
        category: createForm.category,
        is_pinned: createForm.is_pinned,
        target_audience: createForm.target_audience
      });
      if (createForm.image_url) params.append('image_url', createForm.image_url);
      
      await axios.post(
        `${API}/broadcasts?${params.toString()}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Broadcast created!');
      setShowCreateDialog(false);
      setCreateForm({
        title: '',
        content: '',
        category: 'general',
        image_url: '',
        is_pinned: false,
        target_audience: 'all'
      });
      fetchBroadcasts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create broadcast');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteBroadcast = async (broadcastId) => {
    if (!window.confirm('Are you sure you want to delete this broadcast?')) return;
    
    try {
      await axios.delete(`${API}/broadcasts/${broadcastId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Broadcast deleted');
      setSelectedBroadcast(null);
      fetchBroadcasts();
    } catch (error) {
      toast.error('Failed to delete broadcast');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="broadcasts-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase">
            <span className="text-primary">ANNOUNCEMENTS</span>
          </h1>
          <p className="text-gray-400 mt-1">Stay updated with the latest from Bodies By Akeem</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Category Filter */}
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-40 bg-secondary border-transparent">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {Object.entries(categories).map(([key, cat]) => (
                <SelectItem key={key} value={key}>
                  {cat.icon} {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Admin Create Button */}
          {isAdmin && (
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="bg-primary hover:bg-red-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Broadcast
            </Button>
          )}
        </div>
      </div>

      {/* Broadcasts List */}
      {broadcasts.length > 0 ? (
        <div className="space-y-4">
          {broadcasts.map((broadcast) => (
            <div
              key={broadcast.id}
              onClick={() => fetchBroadcastDetail(broadcast.id)}
              className={`card-tactical cursor-pointer hover:border-primary/50 transition-colors ${
                broadcast.is_pinned ? 'border-yellow-500/50' : ''
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Category Icon */}
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center text-2xl"
                  style={{ backgroundColor: `${broadcast.category_info?.color}20` }}
                >
                  {broadcast.category_info?.icon || '📢'}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {broadcast.is_pinned && (
                      <Pin className="w-4 h-4 text-yellow-500" />
                    )}
                    <h3 className="text-lg text-white font-semibold truncate">{broadcast.title}</h3>
                  </div>
                  
                  <p className="text-gray-400 text-sm line-clamp-2 mb-3">{broadcast.content}</p>
                  
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(broadcast.created_at)}
                    </span>
                    
                    <button
                      onClick={(e) => handleLike(broadcast.id, e)}
                      className={`flex items-center gap-1 ${
                        broadcast.is_liked ? 'text-red-500' : 'text-gray-400 hover:text-red-400'
                      }`}
                    >
                      <Heart className={`w-4 h-4 ${broadcast.is_liked ? 'fill-current' : ''}`} />
                      {broadcast.likes_count}
                    </button>
                    
                    <span className="text-gray-400 flex items-center gap-1">
                      <MessageCircle className="w-4 h-4" />
                      {broadcast.comments_count}
                    </span>
                    
                    <span className="text-gray-400 flex items-center gap-1">
                      <Eye className="w-4 h-4" />
                      {broadcast.views_count}
                    </span>
                  </div>
                </div>
                
                <ChevronRight className="w-5 h-5 text-gray-500" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card-tactical text-center py-12">
          <Megaphone className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl text-white font-semibold mb-2">No Announcements Yet</h3>
          <p className="text-gray-400">Check back later for updates from your trainer!</p>
        </div>
      )}

      {/* Broadcast Detail Dialog */}
      <Dialog open={!!selectedBroadcast} onOpenChange={(open) => !open && setSelectedBroadcast(null)}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedBroadcast && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-xl"
                    style={{ backgroundColor: `${selectedBroadcast.category_info?.color}20` }}
                  >
                    {selectedBroadcast.category_info?.icon}
                  </div>
                  <div className="flex-1">
                    <DialogTitle className="text-white flex items-center gap-2">
                      {selectedBroadcast.is_pinned && <Pin className="w-4 h-4 text-yellow-500" />}
                      {selectedBroadcast.title}
                    </DialogTitle>
                    <p className="text-sm text-gray-400">
                      {selectedBroadcast.author_name} • {formatDate(selectedBroadcast.created_at)}
                    </p>
                  </div>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteBroadcast(selectedBroadcast.id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </DialogHeader>
              
              <div className="mt-4 space-y-4">
                {/* Content */}
                <div className="text-gray-300 whitespace-pre-wrap">{selectedBroadcast.content}</div>
                
                {/* Image */}
                {selectedBroadcast.image_url && (
                  <img 
                    src={selectedBroadcast.image_url} 
                    alt="" 
                    className="w-full rounded-sm"
                  />
                )}
                
                {/* Engagement */}
                <div className="flex items-center gap-4 py-3 border-t border-b border-border">
                  <button
                    onClick={() => handleLike(selectedBroadcast.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-sm transition-colors ${
                      selectedBroadcast.is_liked 
                        ? 'bg-red-500/20 text-red-400' 
                        : 'bg-secondary/50 text-gray-400 hover:text-red-400'
                    }`}
                  >
                    <Heart className={`w-5 h-5 ${selectedBroadcast.is_liked ? 'fill-current' : ''}`} />
                    {selectedBroadcast.likes_count} Likes
                  </button>
                  
                  <span className="text-gray-400 flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    {selectedBroadcast.views_count} Views
                  </span>
                </div>
                
                {/* Comments Section */}
                <div>
                  <h4 className="text-white font-semibold mb-3">
                    Comments ({selectedBroadcast.comments?.length || 0})
                  </h4>
                  
                  {/* Add Comment */}
                  <div className="flex gap-2 mb-4">
                    <Input
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Write a comment..."
                      className="bg-secondary border-transparent"
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleComment()}
                    />
                    <Button
                      onClick={handleComment}
                      disabled={!newComment.trim() || submittingComment}
                      className="bg-primary hover:bg-red-700"
                    >
                      {submittingComment ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  
                  {/* Comments List */}
                  <div className="space-y-3">
                    {selectedBroadcast.comments?.map((comment) => (
                      <div key={comment.id} className="p-3 bg-secondary/30 rounded-sm">
                        <div className="flex items-start justify-between">
                          <div>
                            <span className="text-white font-medium">{comment.user_name}</span>
                            <span className="text-gray-500 text-sm ml-2">{formatDate(comment.created_at)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleLikeComment(comment.id)}
                              className={`text-sm flex items-center gap-1 ${
                                comment.is_liked ? 'text-red-400' : 'text-gray-500 hover:text-red-400'
                              }`}
                            >
                              <Heart className={`w-3 h-3 ${comment.is_liked ? 'fill-current' : ''}`} />
                              {comment.likes_count}
                            </button>
                            {(comment.user_id === user?.id || isAdmin) && (
                              <button
                                onClick={() => handleDeleteComment(comment.id)}
                                className="text-gray-500 hover:text-red-400"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="text-gray-300 text-sm mt-1">{comment.content}</p>
                      </div>
                    ))}
                    
                    {(!selectedBroadcast.comments || selectedBroadcast.comments.length === 0) && (
                      <p className="text-gray-500 text-center py-4">No comments yet. Be the first!</p>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Broadcast Dialog (Admin Only) */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Create Broadcast</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-gray-300">Title</Label>
              <Input
                value={createForm.title}
                onChange={(e) => setCreateForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Announcement title"
                className="bg-secondary border-transparent mt-1"
              />
            </div>
            
            <div>
              <Label className="text-gray-300">Content</Label>
              <Textarea
                value={createForm.content}
                onChange={(e) => setCreateForm(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Write your announcement..."
                className="bg-secondary border-transparent mt-1"
                rows={4}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Category</Label>
                <Select
                  value={createForm.category}
                  onValueChange={(v) => setCreateForm(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger className="bg-secondary border-transparent mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(categories).map(([key, cat]) => (
                      <SelectItem key={key} value={key}>
                        {cat.icon} {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-gray-300">Audience</Label>
                <Select
                  value={createForm.target_audience}
                  onValueChange={(v) => setCreateForm(prev => ({ ...prev, target_audience: v }))}
                >
                  <SelectTrigger className="bg-secondary border-transparent mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Members</SelectItem>
                    <SelectItem value="premium">Premium Only</SelectItem>
                    <SelectItem value="free">Free Members</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label className="text-gray-300">Image URL (optional)</Label>
              <Input
                value={createForm.image_url}
                onChange={(e) => setCreateForm(prev => ({ ...prev, image_url: e.target.value }))}
                placeholder="https://..."
                className="bg-secondary border-transparent mt-1"
              />
            </div>
            
            <div className="flex items-center gap-3">
              <Switch
                checked={createForm.is_pinned}
                onCheckedChange={(checked) => setCreateForm(prev => ({ ...prev, is_pinned: checked }))}
              />
              <Label className="text-gray-300">Pin this announcement</Label>
            </div>
            
            <Button
              onClick={handleCreateBroadcast}
              disabled={creating}
              className="w-full bg-primary hover:bg-red-700"
            >
              {creating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Megaphone className="w-4 h-4 mr-2" />
              )}
              Publish Broadcast
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
